import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useWallet, useConnection } from '@solana/wallet-adapter-react'
import { useWalletModal } from '@solana/wallet-adapter-react-ui'
import { PublicKey, Transaction, TransactionInstruction, SystemProgram } from '@solana/web3.js'
import '../styles/staking.css'

// ── On-Chain Constants ──────────────────────────────────────────────────
const PROGRAM_ID = new PublicKey('CSkRjy7gyhWw6izU9jrJUKdwgzCCNM4m8edA4REbjQti')
const SKR_MINT = new PublicKey('SKRbvo6Gf7GondiT3BbTfuRDPqLWei4j2Qy2NPGZhW3')
const CSKR_MINT = new PublicKey('GT6LYQN3fnwLm127ULvYVg5xDvmacfUEah6KQQYaK7Jx')
const POOL_STATE_PDA = new PublicKey('5mj13zoqBwE61ELA6QjRNjcHDf5bvms62Z48dtvgGXCM')
const POOL_VAULT = new PublicKey('28jV6eBH8rsRGeGoaVWcxFvkobfKHH8797nRSLQHTsQt')
const MINT_AUTHORITY = new PublicKey('G2HoxRjhjeX7rHGH6eay866gpxJTLbKsvChpaXbGsfoF')
const QUEUE_ESCROW = new PublicKey('6MXj3HzqJwa66Fxwt6QmTmXaQ4HHmy9JmVQ9qTQWeau2')
const TREASURY = new PublicKey('BLUErbZsh1JNwzySVxhStopvsk47EaDmoGjXnAbQzgiQ')
const TREASURY_SKR_ATA = new PublicKey('6Aq428L6M7LBTGbqdq8GbNVXi5YCXFRBKVtgnJiXmoYw')
const TOKEN_PROGRAM = new PublicKey('TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA')
const ASSOCIATED_TOKEN_PROGRAM = new PublicKey('ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL')

const HELIUS_RPC = 'https://mainnet.helius-rpc.com/?api-key=f8567a96-fef3-4752-88d4-6338fb67e1b0'
const POOL_DISCRIMINATOR = [0x63, 0x61, 0x6e, 0x70, 0x6f, 0x6f, 0x6c, 0x73] // "canpools"
const UNSTAKE_REQ_DISCRIMINATOR = [0x75, 0x6e, 0x73, 0x74, 0x72, 0x65, 0x71, 0x73] // "unstreqs"
const UNSTAKE_REQUEST_SIZE = 92

// Program tags
const TAG_DEPOSIT = 1
const TAG_WITHDRAW = 2
const TAG_INSTANT_UNSTAKE = 13

// ── Helpers ─────────────────────────────────────────────────────────────

function deriveAta(wallet, mint) {
  const [ata] = PublicKey.findProgramAddressSync(
    [wallet.toBuffer(), TOKEN_PROGRAM.toBuffer(), mint.toBuffer()],
    ASSOCIATED_TOKEN_PROGRAM
  )
  return ata
}

function toLamports(amount) {
  return Math.floor(parseFloat(amount) * 1e6)
}

function fromLamports(lamports) {
  return (Number(lamports) / 1e6).toFixed(2)
}

function shortAddress(addr) {
  const s = typeof addr === 'string' ? addr : addr.toBase58()
  return s.slice(0, 4) + '...' + s.slice(-4)
}

// Decode 190-byte pool state (v6 with 8-byte "canpools" discriminator)
function decodePoolState(data) {
  const view = new DataView(data.buffer, data.byteOffset)
  // [0..8]   discriminator "canpools"
  // [8..40]  authority
  // [40..72] pending_authority
  // [72..104] treasury
  const authority = new PublicKey(data.slice(8, 40))
  const treasuryPubkey = new PublicKey(data.slice(72, 104))
  const totalCskrSupply = view.getBigUint64(104, true)
  const totalSkrDeposited = view.getBigUint64(112, true)
  // [120..136] exchange_rate u128 (skip, derive from ratio)
  const depositFeeBps = view.getUint16(136, true)
  // [138] bump, [139] is_initialized
  // [140..148] total_skr_staked_guardian
  // [148..156] pending_guardian_unstake
  // [156..164] unstake_cooldown_end
  // [164..166] target_reserve_bps
  const instantFeeBps = view.getUint16(166, true)
  // [168..172] total_pending_unstakes
  // [172..180] total_escrowed_cskr
  const yieldCommissionBps = data.length >= 182 ? view.getUint16(180, true) : 0

  const ratio = totalCskrSupply > 0n
    ? Number(totalSkrDeposited) / Number(totalCskrSupply)
    : 1.0

  return {
    authority: authority.toBase58(),
    treasury: treasuryPubkey.toBase58(),
    depositFeeBps,
    totalSkrDeposited: Number(totalSkrDeposited),
    totalCskrSupply: Number(totalCskrSupply),
    instantFeeBps,
    yieldCommissionBps,
    ratio,
  }
}

// Build deposit instruction (9 accounts, matches processor.rs deposit)
function buildDepositInstruction(userPubkey, amount) {
  const userSkrAta = deriveAta(userPubkey, SKR_MINT)
  const userCskrAta = deriveAta(userPubkey, CSKR_MINT)

  const data = Buffer.alloc(9)
  data[0] = TAG_DEPOSIT
  data.writeBigUInt64LE(BigInt(amount), 1)

  return new TransactionInstruction({
    programId: PROGRAM_ID,
    keys: [
      { pubkey: userPubkey, isSigner: true, isWritable: true },       // [0] user
      { pubkey: POOL_STATE_PDA, isSigner: false, isWritable: true },  // [1] pool_state
      { pubkey: userSkrAta, isSigner: false, isWritable: true },      // [2] user_skr_ata
      { pubkey: POOL_VAULT, isSigner: false, isWritable: true },      // [3] pool_vault
      { pubkey: CSKR_MINT, isSigner: false, isWritable: true },       // [4] cskr_mint
      { pubkey: userCskrAta, isSigner: false, isWritable: true },     // [5] user_cskr_ata
      { pubkey: MINT_AUTHORITY, isSigner: false, isWritable: false }, // [6] mint_authority
      { pubkey: TREASURY_SKR_ATA, isSigner: false, isWritable: true }, // [7] treasury_skr_ata
      { pubkey: TOKEN_PROGRAM, isSigner: false, isWritable: false },  // [8] token_program
    ],
    data,
  })
}

// Build deposit instruction with peer-match (13 accounts)
function buildDepositWithPeerMatchInstruction(userPubkey, amount, unstakeRequestPda, requestorSkrAta, requestorPubkey) {
  const userSkrAta = deriveAta(userPubkey, SKR_MINT)
  const userCskrAta = deriveAta(userPubkey, CSKR_MINT)

  const data = Buffer.alloc(9)
  data[0] = TAG_DEPOSIT
  data.writeBigUInt64LE(BigInt(amount), 1)

  return new TransactionInstruction({
    programId: PROGRAM_ID,
    keys: [
      { pubkey: userPubkey, isSigner: true, isWritable: true },       // [0] user
      { pubkey: POOL_STATE_PDA, isSigner: false, isWritable: true },  // [1] pool_state
      { pubkey: userSkrAta, isSigner: false, isWritable: true },      // [2] user_skr_ata
      { pubkey: POOL_VAULT, isSigner: false, isWritable: true },      // [3] pool_vault
      { pubkey: CSKR_MINT, isSigner: false, isWritable: true },       // [4] cskr_mint
      { pubkey: userCskrAta, isSigner: false, isWritable: true },     // [5] user_cskr_ata
      { pubkey: MINT_AUTHORITY, isSigner: false, isWritable: false }, // [6] mint_authority
      { pubkey: TREASURY_SKR_ATA, isSigner: false, isWritable: true }, // [7] treasury_skr_ata
      { pubkey: TOKEN_PROGRAM, isSigner: false, isWritable: false },  // [8] token_program
      // Peer-match accounts
      { pubkey: QUEUE_ESCROW, isSigner: false, isWritable: true },    // [9] queue_escrow
      { pubkey: unstakeRequestPda, isSigner: false, isWritable: true }, // [10] unstake_request
      { pubkey: requestorSkrAta, isSigner: false, isWritable: true }, // [11] requestor_skr_ata
      { pubkey: requestorPubkey, isSigner: false, isWritable: true }, // [12] requestor
    ],
    data,
  })
}

// Build withdraw instruction
function buildWithdrawInstruction(userPubkey, amount) {
  const userSkrAta = deriveAta(userPubkey, SKR_MINT)
  const userCskrAta = deriveAta(userPubkey, CSKR_MINT)

  const data = Buffer.alloc(9)
  data[0] = TAG_WITHDRAW
  data.writeBigUInt64LE(BigInt(amount), 1)

  return new TransactionInstruction({
    programId: PROGRAM_ID,
    keys: [
      { pubkey: userPubkey, isSigner: true, isWritable: true },
      { pubkey: POOL_STATE_PDA, isSigner: false, isWritable: true },
      { pubkey: userCskrAta, isSigner: false, isWritable: true },
      { pubkey: CSKR_MINT, isSigner: false, isWritable: true },
      { pubkey: POOL_VAULT, isSigner: false, isWritable: true },
      { pubkey: userSkrAta, isSigner: false, isWritable: true },
      { pubkey: MINT_AUTHORITY, isSigner: false, isWritable: false },
      { pubkey: TOKEN_PROGRAM, isSigner: false, isWritable: false },
    ],
    data,
  })
}

// Derive unstake request PDA: seeds=["unstake_req", user]
function deriveUnstakeRequest(userPubkey) {
  const [pda] = PublicKey.findProgramAddressSync(
    [Buffer.from('unstake_req'), userPubkey.toBuffer()],
    PROGRAM_ID
  )
  return pda
}

// Build instant unstake instruction (9 accounts, 12-byte data)
function buildInstantUnstakeInstruction(userPubkey, amount, tipBps = 0, tier = 0) {
  const userSkrAta = deriveAta(userPubkey, SKR_MINT)
  const userCskrAta = deriveAta(userPubkey, CSKR_MINT)
  const unstakeReq = deriveUnstakeRequest(userPubkey)

  // [tag:1] [cskr_amount:u64(8)] [tip_bps:u16(2)] [tier:u8(1)] = 12 bytes
  const data = Buffer.alloc(12)
  data[0] = TAG_INSTANT_UNSTAKE
  data.writeBigUInt64LE(BigInt(amount), 1)
  data.writeUInt16LE(tipBps, 9)
  data[11] = tier

  return new TransactionInstruction({
    programId: PROGRAM_ID,
    keys: [
      { pubkey: userPubkey, isSigner: true, isWritable: true },       // [0] user
      { pubkey: POOL_STATE_PDA, isSigner: false, isWritable: true },  // [1] pool_state
      { pubkey: userCskrAta, isSigner: false, isWritable: true },     // [2] user_cskr_ata
      { pubkey: QUEUE_ESCROW, isSigner: false, isWritable: true },    // [3] queue_escrow
      { pubkey: unstakeReq, isSigner: false, isWritable: true },      // [4] unstake_request PDA
      { pubkey: userSkrAta, isSigner: false, isWritable: true },      // [5] user_skr_ata
      { pubkey: MINT_AUTHORITY, isSigner: false, isWritable: false }, // [6] mint_authority
      { pubkey: TOKEN_PROGRAM, isSigner: false, isWritable: false },  // [7] token_program
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false }, // [8] system_program
    ],
    data,
  })
}

// ── Component ───────────────────────────────────────────────────────────

export default function StakingDashboard() {
  const { connection } = useConnection()
  const { publicKey, signTransaction, connected } = useWallet()
  const { setVisible } = useWalletModal()

  const [poolState, setPoolState] = useState(null)
  const [skrBalance, setSkrBalance] = useState(0)
  const [cskrBalance, setCskrBalance] = useState(0)
  const [activeTab, setActiveTab] = useState('stake')
  const [inputAmount, setInputAmount] = useState('')
  const [loading, setLoading] = useState(false)
  const [txResult, setTxResult] = useState(null) // { type, message, sig }
  const [refreshKey, setRefreshKey] = useState(0)

  // Use Helius RPC for reads (more reliable)
  const rpcFetch = useCallback(async (method, params) => {
    const res = await fetch(HELIUS_RPC, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ jsonrpc: '2.0', id: 1, method, params }),
    })
    const json = await res.json()
    return json.result
  }, [])

  // Fetch the best unstake request to peer-match (skip self)
  const fetchBestUnstakeRequest = useCallback(async (depositorPubkey) => {
    try {
      const discBase64 = btoa(String.fromCharCode(...UNSTAKE_REQ_DISCRIMINATOR))
      const result = await rpcFetch('getProgramAccounts', [
        PROGRAM_ID.toBase58(),
        {
          encoding: 'base64',
          commitment: 'confirmed',
          filters: [
            { memcmp: { offset: 0, bytes: discBase64, encoding: 'base64' } },
            { dataSize: UNSTAKE_REQUEST_SIZE },
          ],
        },
      ])
      if (!result || result.length === 0) return null

      const requests = result.map(item => {
        try {
          const raw = Buffer.from(item.account.data[0], 'base64')
          const user = new PublicKey(raw.slice(8, 40))
          const userSkrAta = new PublicKey(raw.slice(40, 72))
          const cskrAmount = Number(raw.readBigUInt64LE(72))
          const createdAt = Number(raw.readBigInt64LE(80))
          const tipBps = raw.readUInt16LE(89)
          const tier = raw.readUInt8(91)
          return { user, userSkrAta, cskrAmount, createdAt, tipBps, tier }
        } catch { return null }
      }).filter(r => r !== null)

      // Filter out self-match
      const depositorBase58 = depositorPubkey.toBase58()
      const eligible = requests.filter(r => r.user.toBase58() !== depositorBase58)
      if (eligible.length === 0) return null

      // Sort: highest tier → highest tip → oldest (FIFO)
      eligible.sort((a, b) => {
        if (b.tier !== a.tier) return b.tier - a.tier
        if (b.tipBps !== a.tipBps) return b.tipBps - a.tipBps
        return a.createdAt - b.createdAt
      })

      return eligible[0]
    } catch (e) {
      console.warn('Peer-match lookup failed:', e)
      return null
    }
  }, [rpcFetch])

  // Fetch pool state
  const fetchPoolState = useCallback(async () => {
    try {
      const result = await rpcFetch('getAccountInfo', [
        POOL_STATE_PDA.toBase58(),
        { encoding: 'base64' },
      ])
      if (result?.value?.data) {
        const raw = Buffer.from(result.value.data[0], 'base64')
        setPoolState(decodePoolState(new Uint8Array(raw)))
      }
    } catch (e) {
      console.error('Failed to fetch pool state:', e)
    }
  }, [rpcFetch])

  // Fetch user balances
  const fetchBalances = useCallback(async () => {
    if (!publicKey) {
      setSkrBalance(0)
      setCskrBalance(0)
      return
    }
    try {
      const skrAta = deriveAta(publicKey, SKR_MINT)
      const cskrAta = deriveAta(publicKey, CSKR_MINT)

      const [skrResult, cskrResult] = await Promise.all([
        rpcFetch('getTokenAccountBalance', [skrAta.toBase58()]),
        rpcFetch('getTokenAccountBalance', [cskrAta.toBase58()]),
      ])

      setSkrBalance(skrResult?.value?.amount ? Number(skrResult.value.amount) : 0)
      setCskrBalance(cskrResult?.value?.amount ? Number(cskrResult.value.amount) : 0)
    } catch (e) {
      console.error('Failed to fetch balances:', e)
    }
  }, [publicKey, rpcFetch])

  // Initial + refresh
  useEffect(() => {
    fetchPoolState()
  }, [fetchPoolState, refreshKey])

  useEffect(() => {
    fetchBalances()
  }, [fetchBalances, refreshKey])

  // Auto-refresh every 30s
  useEffect(() => {
    const interval = setInterval(() => setRefreshKey(k => k + 1), 30000)
    return () => clearInterval(interval)
  }, [])

  // Execute staking transaction
  const executeTransaction = async () => {
    if (!publicKey || !signTransaction || !inputAmount) return

    setLoading(true)
    setTxResult(null)

    try {
      const amount = toLamports(inputAmount)
      if (amount <= 0) throw new Error('Amount must be greater than 0')

      let ix
      if (activeTab === 'stake') {
        // Try peer-match deposit first
        try {
          const bestRequest = await fetchBestUnstakeRequest(publicKey)
          if (bestRequest) {
            const unstakeReqPda = deriveUnstakeRequest(bestRequest.user)
            ix = buildDepositWithPeerMatchInstruction(
              publicKey, amount, unstakeReqPda, bestRequest.userSkrAta, bestRequest.user
            )
            console.log('Peer-match: filling unstake from', bestRequest.user.toBase58(),
              `(${bestRequest.cskrAmount / 1e6} cSKR, tip=${bestRequest.tipBps}bps)`)
          } else {
            ix = buildDepositInstruction(publicKey, amount)
          }
        } catch (e) {
          console.warn('Peer-match failed, using standard deposit:', e.message)
          ix = buildDepositInstruction(publicKey, amount)
        }
      } else if (activeTab === 'unstake') {
        ix = buildWithdrawInstruction(publicKey, amount)
      } else {
        ix = buildInstantUnstakeInstruction(publicKey, amount)
      }

      const tx = new Transaction().add(ix)
      const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed')
      tx.recentBlockhash = blockhash
      tx.feePayer = publicKey

      const signed = await signTransaction(tx)
      const sig = await connection.sendRawTransaction(signed.serialize(), {
        skipPreflight: false,
        preflightCommitment: 'confirmed',
      })

      await connection.confirmTransaction({ signature: sig, blockhash, lastValidBlockHeight }, 'confirmed')

      setTxResult({
        type: 'success',
        message: `${activeTab === 'stake' ? 'Staked' : 'Unstaked'} ${inputAmount} ${activeTab === 'stake' ? 'SKR' : 'cSKR'}`,
        sig,
      })

      setInputAmount('')
      setRefreshKey(k => k + 1)
    } catch (e) {
      console.error('Transaction failed:', e)
      setTxResult({ type: 'error', message: e.message || 'Transaction failed' })
    } finally {
      setLoading(false)
    }
  }

  // Computed values
  const conversionAmount = useMemo(() => {
    const amt = parseFloat(inputAmount) || 0
    if (!poolState || amt === 0) return null
    if (activeTab === 'stake') {
      return (amt / poolState.ratio).toFixed(4)
    }
    return (amt * poolState.ratio).toFixed(4)
  }, [inputAmount, poolState, activeTab])

  const maxBalance = activeTab === 'stake' ? skrBalance : cskrBalance
  const inputToken = activeTab === 'stake' ? 'SKR' : 'cSKR'
  const outputToken = activeTab === 'stake' ? 'cSKR' : 'SKR'
  const isValid = connected && parseFloat(inputAmount) > 0 && toLamports(inputAmount) <= maxBalance

  return (
    <div className="staking-page">
      {/* Navigation */}
      <nav className="staking-nav">
        <a className="staking-nav-brand" href="/staking">
          <div className="logo">C</div>
          <span>Canary Staking</span>
        </a>
        <div className="staking-nav-links">
          <a href="/staking" className="active">Dashboard</a>
          <a href="/staking/docs">Documentation</a>
          <a href="/rewards">Epoch Rewards</a>
        </div>
        <div className="staking-nav-wallet">
          {connected ? (
            <button className="wallet-btn connected">
              {shortAddress(publicKey)}
            </button>
          ) : (
            <button className="wallet-btn" onClick={() => setVisible(true)}>
              Connect Wallet
            </button>
          )}
        </div>
      </nav>

      <div className="staking-container">
        {/* Hero */}
        <div className="staking-hero">
          <h1>SKR Liquid Staking</h1>
          <p>Stake SKR to receive cSKR - a liquid receipt token that earns yield automatically</p>
          <div className="badge">
            <span className="dot" />
            Mainnet Live
          </div>
        </div>

        {/* Stats */}
        <div className="staking-stats">
          <div className="perch-card stat-card">
            <div className="stat-label">Total Staked</div>
            <div className="stat-value cyan">
              {poolState ? fromLamports(poolState.totalSkrDeposited) : '-'} SKR
            </div>
          </div>
          <div className="perch-card stat-card">
            <div className="stat-label">Exchange Rate</div>
            <div className="stat-value purple">
              {poolState ? `1 cSKR = ${poolState.ratio.toFixed(4)} SKR` : '-'}
            </div>
          </div>
          <div className="perch-card stat-card">
            <div className="stat-label">cSKR Supply</div>
            <div className="stat-value cyan">
              {poolState ? fromLamports(poolState.totalCskrSupply) : '-'}
            </div>
          </div>
          <div className="perch-card stat-card">
            <div className="stat-label">APY</div>
            <div className="stat-value emerald">7.2%</div>
            <div className="stat-sub">Commission: {poolState ? `${poolState.yieldCommissionBps / 100}%` : '-'}</div>
          </div>
        </div>

        {/* Main Layout */}
        <div className="staking-layout">
          {/* Left: Dashboard */}
          <div className="staking-dashboard">
            {/* Holdings */}
            {connected && (
              <div className="perch-card" style={{ marginBottom: 24 }}>
                <div className="card-highlight" />
                <div className="section-label">Your Holdings</div>
                <div className="holdings-row">
                  <div className="token-info">
                    <div className="token-icon skr">S</div>
                    <div>
                      <div className="token-name">SKR</div>
                      <div className="token-label">Canary Token</div>
                    </div>
                  </div>
                  <div className="token-amount">
                    <div className="amount">{fromLamports(skrBalance)}</div>
                  </div>
                </div>
                <div className="holdings-divider" />
                <div className="holdings-row">
                  <div className="token-info">
                    <div className="token-icon cskr">cS</div>
                    <div>
                      <div className="token-name">cSKR</div>
                      <div className="token-label">Staked SKR</div>
                    </div>
                  </div>
                  <div className="token-amount">
                    <div className="amount">{fromLamports(cskrBalance)}</div>
                    {poolState && cskrBalance > 0 && (
                      <div className="usd">≈ {fromLamports(Math.floor(cskrBalance * poolState.ratio))} SKR</div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Pool Info */}
            <div className="perch-card">
              <div className="section-label">Pool Information</div>
              <div className="pool-info-grid">
                <div className="pool-info-row">
                  <span className="label">Program</span>
                  <span className="value mono">
                    <a href={`https://orb.helius.dev/address/${PROGRAM_ID.toBase58()}?cluster=mainnet-beta`} target="_blank" rel="noopener noreferrer">
                      {shortAddress(PROGRAM_ID)}
                    </a>
                  </span>
                </div>
                <div className="pool-info-row">
                  <span className="label">Pool State</span>
                  <span className="value mono">
                    <a href={`https://orb.helius.dev/address/${POOL_STATE_PDA.toBase58()}?cluster=mainnet-beta`} target="_blank" rel="noopener noreferrer">
                      {shortAddress(POOL_STATE_PDA)}
                    </a>
                  </span>
                </div>
                <div className="pool-info-row">
                  <span className="label">SKR Mint</span>
                  <span className="value mono">
                    <a href={`https://orb.helius.dev/token/${SKR_MINT.toBase58()}?cluster=mainnet-beta`} target="_blank" rel="noopener noreferrer">
                      {shortAddress(SKR_MINT)}
                    </a>
                  </span>
                </div>
                <div className="pool-info-row">
                  <span className="label">cSKR Mint</span>
                  <span className="value mono">
                    <a href={`https://orb.helius.dev/token/${CSKR_MINT.toBase58()}?cluster=mainnet-beta`} target="_blank" rel="noopener noreferrer">
                      {shortAddress(CSKR_MINT)}
                    </a>
                  </span>
                </div>
                <div className="pool-info-row">
                  <span className="label">Pool Version</span>
                  <span className="value">6</span>
                </div>
                <div className="pool-info-row">
                  <span className="label">Deposit Fee</span>
                  <span className="value">{poolState ? `${poolState.depositFeeBps / 100}%` : '-'}</span>
                </div>
                <div className="pool-info-row">
                  <span className="label">Instant Unstake Fee</span>
                  <span className="value">{poolState ? `${poolState.instantFeeBps / 100}%` : '-'}</span>
                </div>
                <div className="pool-info-row">
                  <span className="label">Yield Commission</span>
                  <span className="value">{poolState ? `${poolState.yieldCommissionBps / 100}%` : '-'}</span>
                </div>
                <div className="pool-info-row">
                  <span className="label">Treasury</span>
                  <span className="value mono">
                    <a href={`https://orb.helius.dev/address/${TREASURY.toBase58()}?cluster=mainnet-beta`} target="_blank" rel="noopener noreferrer">
                      {shortAddress(TREASURY)}
                    </a>
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Staking Panel */}
          <div className="staking-panel">
            <div className="perch-card glow">
              <div className="card-highlight" />

              <div className="staking-tabs">
                <button
                  className={activeTab === 'stake' ? 'active' : ''}
                  onClick={() => { setActiveTab('stake'); setInputAmount(''); setTxResult(null) }}
                >
                  Stake
                </button>
                <button
                  className={activeTab === 'unstake' ? 'active' : ''}
                  onClick={() => { setActiveTab('unstake'); setInputAmount(''); setTxResult(null) }}
                >
                  Unstake
                </button>
                <button
                  className={activeTab === 'instant' ? 'active' : ''}
                  onClick={() => { setActiveTab('instant'); setInputAmount(''); setTxResult(null) }}
                >
                  Instant
                </button>
              </div>

              <div className="staking-input-group">
                <div className="staking-input-header">
                  <label>{activeTab === 'stake' ? 'You Stake' : 'You Unstake'}</label>
                  <div className="balance">
                    Balance: <span onClick={() => setInputAmount(fromLamports(maxBalance))}>
                      {fromLamports(maxBalance)} {inputToken}
                    </span>
                  </div>
                </div>
                <div className="staking-input-wrap">
                  <button className="max-btn" onClick={() => setInputAmount(fromLamports(maxBalance))}>
                    MAX
                  </button>
                  <input
                    type="number"
                    placeholder="0.00"
                    value={inputAmount}
                    onChange={e => setInputAmount(e.target.value)}
                    min="0"
                    step="0.01"
                  />
                  <div className="token-badge">{inputToken}</div>
                </div>
              </div>

              {conversionAmount && (
                <div className="staking-conversion">
                  <span className="icon">↓</span>
                  You receive ≈ <span className="amount">{conversionAmount} {outputToken}</span>
                  {activeTab === 'instant' && poolState && (
                    <span> (fee: {poolState.instantFeeBps / 100}%)</span>
                  )}
                </div>
              )}

              <button
                className={`staking-cta ${isValid && !loading ? 'enabled' : 'disabled'}`}
                onClick={connected ? executeTransaction : () => setVisible(true)}
                disabled={connected && (!isValid || loading)}
              >
                {loading ? (
                  <span className="spinner" />
                ) : !connected ? (
                  'Connect Wallet'
                ) : activeTab === 'stake' ? (
                  'Stake SKR → cSKR'
                ) : activeTab === 'unstake' ? (
                  'Unstake cSKR → SKR'
                ) : (
                  'Instant Unstake (3% fee)'
                )}
              </button>

              {txResult && (
                <div className={`tx-status ${txResult.type}`}>
                  <span className="icon">{txResult.type === 'success' ? '✓' : '✗'}</span>
                  <div className="message">
                    {txResult.message}
                    {txResult.sig && (
                      <>
                        {' - '}
                        <a
                          href={`https://orb.helius.dev/tx/${txResult.sig}?cluster=mainnet-beta`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View TX
                        </a>
                      </>
                    )}
                  </div>
                </div>
              )}

              <div className="info-banner">
                <p>
                  <strong>cSKR</strong> is liquid staked SKR that earns rewards while remaining
                  transferable and usable in DeFi. The exchange rate increases over time as
                  staking rewards accrue to the pool.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
